import { cn } from '@neo/lib/utils'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import type { useMousePosition } from '@shared/hooks/use-mouse-position'
import type { Node } from '@xyflow/react'
import type { FlowNodeConfig } from '../config/panel.config'
import { FlowNodeDescriptions } from '../constants'
import type { FlowNodeTypes, NodeDataBase } from '../types'

interface CandidateNodeProps {
  candidateNode: Node<NodeDataBase<unknown>>
  mousePosition: ReturnType<typeof useMousePosition>['mousePosition']
  nodeConfig: FlowNodeConfig
}

export const CandidateNode = ({ candidateNode, mousePosition, nodeConfig }: CandidateNodeProps) => {
  const nodeName = candidateNode.data?.meta?.name || candidateNode.type || 'Unknown'
  const nodeDescription = FlowNodeDescriptions[candidateNode.type as FlowNodeTypes]
  const meta = candidateNode.data?.meta || { name: candidateNode.type, shouldHighlight: false }

  return (
    <div
      data-testid="candidate-node"
      style={{
        position: 'absolute',
        left: mousePosition.x - 118, // Center the wider node
        top: mousePosition.y - 70,
        zIndex: 10000,
      }}
    >
      <div
        className={cn('flow-node group relative rounded-2xl transition-colors p-[1.2px]')}
        data-testid={`node-${candidateNode.id}`}
      >
        <div
          className={cn(
            'relative flex flex-col gap-1 w-[236px] bg-background z-10 transition-colors border rounded-2xl',
            'shadow-lg border-dashed border-st2',
          )}
        >
          <div className="flex flex-col gap-1 w-full bg-background rounded-2xl">
            <div className="min-h-8 p-3 rounded-xl text-s-500">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-bg2 pr-3 rounded-md overflow-hidden">
                  <div className={`rounded p-1.5 ${nodeConfig.class}`}>{nodeConfig.icon}</div>
                  <TruncatedTextWithTooltip
                    tooltipContent={nodeName}
                    className="font-inter text-p-500"
                  >
                    {nodeName}
                  </TruncatedTextWithTooltip>
                </div>
              </div>

              {nodeDescription && (
                <div className="font-inter text-s-400 text-secondary mt-2">{nodeDescription}</div>
              )}

              <div className="flex justify-between mt-2 items-center">
                <div className="font-jetbrains text-label uppercase tracking-normal text-tertiary">
                  {meta.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
